import { useState } from "react";

interface CancelAppointmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  cancellationComment: string;
  setcancellationComment: (value: string) => void;
}

const CancelAppointmentModal: React.FC<CancelAppointmentModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  cancellationComment,
  setcancellationComment,
}) => {
  const [error, setError] = useState("");
  const [selectedReason, setSelectedReason] = useState("");

  const handleConfirm = () => {
    if (selectedReason === "") {
      setError("Please select a cancellation reason.");
      return;
    }

    if (selectedReason === "other" && !cancellationComment.trim()) {
      setError("Please provide details for other reason.");
      return;
    }

    // If "Improper behavior" is selected, use that as the reason
    // Otherwise use the custom comment
    const finalReason = selectedReason === "improper" 
      ? "Improper behavior" 
      : cancellationComment;

    onConfirm(finalReason);
    setError("");
    onClose();
  };

  const handleReasonChange = (reason: string) => {
    setSelectedReason(reason);
    setError("");
    
    // Clear comment if switching to "Improper behavior"
    if (reason === "improper") {
      setcancellationComment("");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white p-6 rounded-lg shadow-lg w-96">
        <h2 className="text-lg font-bold mb-3">Cancel Appointment</h2>
        <p className="text-sm text-gray-600 mb-3">
          Please select the reason for cancellation:
        </p>

        <div className="space-y-2 mb-3">
          <div className="flex items-center">
            <input
              type="radio"
              id="improper"
              name="cancellationReason"
              value="improper"
              checked={selectedReason === "improper"}
              onChange={() => handleReasonChange("improper")}
              className="mr-2"
            />
            <label htmlFor="improper">Improper behavior</label>
          </div>
          
          <div className="flex items-center">
            <input
              type="radio"
              id="other"
              name="cancellationReason"
              value="other"
              checked={selectedReason === "other"}
              onChange={() => handleReasonChange("other")}
              className="mr-2"
            />
            <label htmlFor="other">Some other reason</label>
          </div>
        </div>

        {selectedReason === "other" && (
          <textarea
            className="w-full mt-2 p-2 border rounded-md"
            rows={3}
            value={cancellationComment}
            onChange={(e) => {
              setcancellationComment(e.target.value);
              setError(""); // Clear error when user types
            }}
            placeholder="Please explain the reason..."
          />
        )}

        {/* Show error message if needed */}
        {error && <p className="text-red-500 text-sm mt-1">{error}</p>}

        <div className="flex justify-end gap-2 mt-4">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-300 rounded-md hover:bg-gray-400"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className={`px-4 py-2 text-white rounded-md ${
              (selectedReason === "improper" || (selectedReason === "other" && cancellationComment.trim()))
                ? "bg-red-500 hover:bg-red-600"
                : "bg-gray-400 cursor-not-allowed"
            }`}
            disabled={!(selectedReason === "improper" || (selectedReason === "other" && cancellationComment.trim()))}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

export default CancelAppointmentModal;